/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  MagnifyingGlass, Calendar, User, ArrowRight, DownloadSimple, CheckCircle,
  Tag, Newspaper, FileText, Star, X, ArrowLeft, Clock,
} from "@phosphor-icons/react";
import {
  MarketingNav, MarketingFooter, Eyebrow, BTN_GREEN,
} from "./_shared";
import {
  useJsonLd, useMeta, publicationSchema, collectionSchema, breadcrumbSchema,
} from "../../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  newsroom: {
    kinds: ["newsroom"],
    title: "Newsroom",
    sub: "Company updates, press releases, and industry insights.",
    accent: "#0A2540", testid: "page-newsroom",
    Icon: Newspaper,
    basePath: "/newsroom",
  },
  white_papers: {
    kinds: ["white_paper", "research"],
    title: "White Papers & Research",
    sub: "Research-grade frameworks, regulatory manuals, and ESG insights.",
    accent: "#2D5A27", testid: "page-white-papers",
    Icon: FileText,
    basePath: "/white-papers",
  },
  csr: {
    kinds: ["csr_report"],
    title: "CSR & Sustainability Reports",
    sub: "Third-party verified impact reports.",
    accent: "#2D5A27", testid: "page-csr-reports",
    Icon: FileText,
    basePath: "/csr-sustainability",
  },
  security: {
    kinds: ["security_doc"],
    title: "Security Documentation",
    sub: "Compliance frameworks and security documentation.",
    accent: "#B12A1E", testid: "page-security-docs",
    Icon: FileText,
    basePath: "/security-compliance",
  },
};

// ============================================================================
// LISTING PAGE
// ============================================================================
export function PublicationsListing({ variant = "newsroom" }) {
  const meta = KIND_META[variant];
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);
  const pageUrl = typeof window !== "undefined"
    ? `${window.location.origin}${meta.basePath}` : "";

  useMeta({
    title: `Rescigo · ${meta.title}`,
    description: meta.sub,
    url: pageUrl,
  });
  useJsonLd(collectionSchema({
    name: meta.title, description: meta.sub,
    url: pageUrl, items: rows,
  }), `collection-${variant}`);
  useJsonLd(breadcrumbSchema([
    { name: "Home", url: typeof window !== "undefined" ? window.location.origin : "" },
    { name: meta.title, url: pageUrl },
  ]), `breadcrumb-${variant}`);

  useEffect(() => { window.scrollTo(0, 0); }, [variant]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const requests = meta.kinds.map(k => axios.get(`${API}/publications`, { params: { kind: k } }));
        const responses = await Promise.all(requests);
        const merged = responses.flatMap(r => r.data);
        merged.sort((a, b) => new Date(b.published_at || 0) - new Date(a.published_at || 0));
        setRows(merged);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [variant]);

  const categories = useMemo(
    () => [...new Set(rows.map(r => r.category).filter(Boolean))],
    [rows]
  );
  const filtered = useMemo(() => rows.filter(r =>
    (!cat || r.category === cat) &&
    (!q || (r.title + " " + (r.summary || "")).toLowerCase().includes(q.toLowerCase()))
  ), [rows, cat, q]);
  const featured = filtered.filter(r => r.featured);
  const rest = filtered.filter(r => !r.featured);

  return (
    <div className="min-h-screen bg-white" data-testid={meta.testid}>
      <MarketingNav/>

      {/* HERO */}
      <section className="pt-36 pb-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-10 items-end">
          <div className="md:col-span-2">
            <Eyebrow color={meta.accent}><meta.Icon size={11} weight="bold" className="inline mr-1"/> {meta.title}</Eyebrow>
            <h1 className="font-heading text-5xl md:text-6xl mt-4 text-slate-900 leading-[1.05]">
              {variant === "newsroom" ? (<><span className="italic">Stories</span> from the frontline of waste intelligence.</>)
                : (<>Evidence-grade <span className="italic" style={{ color: meta.accent }}>research</span> you can cite.</>)}
            </h1>
            <p className="text-lg text-slate-600 mt-5 max-w-2xl leading-relaxed">{meta.sub}</p>
          </div>
          <div className="bg-white rounded-xl ring-1 ring-slate-200 p-4">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search articles, topics…"
                     data-testid={`${meta.testid}-search`}
                     className="w-full h-11 pl-9 pr-3 border border-slate-300 rounded-lg text-sm outline-none focus:border-slate-800"/>
            </div>
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                <FilterChip active={!cat} onClick={() => setCat("")}>All</FilterChip>
                {categories.map(c => (
                  <FilterChip key={c} active={cat === c} onClick={() => setCat(c)}>{c}</FilterChip>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FEATURED + GRID */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-12">
          {loading && <div className="text-center py-12 text-slate-500">Loading…</div>}

          {!loading && featured.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-amber-700 flex items-center gap-2 mb-4">
                <Star size={11} weight="fill"/> Featured
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {featured.slice(0, 2).map(p => (
                  <FeaturedCard key={p.id} p={p} basePath={meta.basePath} accent={meta.accent}/>
                ))}
              </div>
            </div>
          )}

          {!loading && rest.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500 mb-4">Latest publications</div>
              <div className="grid md:grid-cols-3 gap-5">
                {rest.map(p => (
                  <PubCard key={p.id} p={p} basePath={meta.basePath} accent={meta.accent}/>
                ))}
              </div>
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-14 h-14 rounded-full bg-slate-100 grid place-items-center mx-auto mb-4">
                <meta.Icon size={22} weight="duotone" className="text-slate-400"/>
              </div>
              <div className="font-heading text-xl text-slate-900">No publications yet.</div>
              <div className="text-sm text-slate-500 mt-2">Check back soon — we publish regularly.</div>
            </div>
          )}
        </div>
      </section>

      <MarketingFooter/>
    </div>
  );
}

// ============================================================================
// DETAIL PAGE
// ============================================================================
export function PublicationDetail({ variant = "newsroom" }) {
  const meta = KIND_META[variant];
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [showGate, setShowGate] = useState(false);
  const pageUrl = typeof window !== "undefined"
    ? `${window.location.origin}${meta.basePath}/${slug}` : "";

  useMeta({
    title: p ? `Rescigo · ${p.seo_title || p.title}` : undefined,
    description: p?.seo_description || p?.summary,
    image: p?.cover_url,
    url: pageUrl,
  });
  useJsonLd(publicationSchema(p, pageUrl), `pub-${slug}`);
  useJsonLd(p ? breadcrumbSchema([
    { name: "Home", url: typeof window !== "undefined" ? window.location.origin : "" },
    { name: meta.title, url: typeof window !== "undefined" ? `${window.location.origin}${meta.basePath}` : "" },
    { name: p.title, url: pageUrl },
  ]) : null, `bcrumb-${slug}`);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => {
    axios.get(`${API}/publications/${slug}`)
      .then(r => setP(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (err) return (
    <div className="min-h-screen bg-white">
      <MarketingNav/>
      <div className="pt-40 pb-32 text-center">
        <div className="font-heading text-3xl text-slate-900">Publication not found</div>
        <button onClick={() => nav(meta.basePath)} className="mt-6 text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
          <ArrowLeft size={12}/> Back to {meta.title}
        </button>
      </div>
      <MarketingFooter/>
    </div>
  );
  if (!p) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-white" data-testid={`pub-detail-${slug}`}>
      <MarketingNav/>

      {/* Header band */}
      <section className="pt-32 pb-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <Link to={meta.basePath} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
            <ArrowLeft size={12}/> {meta.title}
          </Link>
          <div className="mt-6 flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-slate-500">
            {p.category && <span className="inline-flex items-center gap-1"><Tag size={11}/> {p.category}</span>}
            {p.published_at && <span className="inline-flex items-center gap-1"><Calendar size={11}/> {new Date(p.published_at).toLocaleDateString()}</span>}
            {p.author && <span className="inline-flex items-center gap-1"><User size={11}/> {p.author}</span>}
          </div>
          <h1 className="font-heading text-4xl md:text-6xl mt-5 text-slate-900 leading-[1.05]">{p.title}</h1>
          {p.subtitle && <p className="text-xl text-slate-600 mt-5 leading-relaxed">{p.subtitle}</p>}
        </div>
      </section>

      {/* Cover + body */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          {p.cover_url && (
            <motion.img initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                         src={p.cover_url} alt={p.title}
                         className="w-full rounded-2xl mb-10 aspect-[16/9] object-cover ring-1 ring-slate-200"/>
          )}

          {/* Summary block */}
          {p.summary && (
            <div className="bg-slate-50 border-l-4 rounded-r-lg p-5 mb-10" style={{ borderColor: meta.accent }}>
              <div className="text-sm text-slate-700 leading-relaxed">{p.summary}</div>
            </div>
          )}

          {/* Key highlights */}
          {p.highlights?.length > 0 && (
            <div className="mb-10">
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500 mb-3">Key highlights</div>
              <ul className="space-y-2.5">
                {p.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-slate-800">
                    <CheckCircle size={16} weight="fill" className="mt-0.5 flex-none" style={{ color: meta.accent }}/>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Body HTML */}
          {p.body_html && (
            <div className="prose prose-slate max-w-none prose-headings:font-heading prose-a:text-[#2D5A27]"
                 dangerouslySetInnerHTML={{ __html: p.body_html }}/>
          )}

          {/* Download / CTA */}
          {p.pdf_url && (
            <div className="mt-12 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 border border-slate-200 flex items-center gap-6">
              <div className="w-14 h-14 rounded-xl grid place-items-center text-white flex-none" style={{ background: meta.accent }}>
                <FileText size={22} weight="duotone"/>
              </div>
              <div className="flex-1">
                <div className="font-heading text-xl text-slate-900">Download the full PDF</div>
                <div className="text-sm text-slate-600 mt-1">{p.download_gate ? "Tell us who you are — we'll share the full report instantly." : "Free to download. No sign-up required."}</div>
              </div>
              {p.download_gate ? (
                <button onClick={() => setShowGate(true)} data-testid="pub-download-btn"
                        className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-white text-sm font-medium whitespace-nowrap"
                        style={{ background: meta.accent }}>
                  <DownloadSimple size={13}/> Download
                </button>
              ) : (
                <a href={p.pdf_url} target="_blank" rel="noreferrer" data-testid="pub-download-btn"
                   className="inline-flex items-center gap-2 h-11 px-5 rounded-full text-white text-sm font-medium whitespace-nowrap"
                   style={{ background: meta.accent }}>
                  <DownloadSimple size={13}/> Download
                </a>
              )}
            </div>
          )}

          {p.cta_label && p.cta_url && (
            <div className="mt-6">
              <Link to={p.cta_url} className={BTN_GREEN}>
                {p.cta_label} <ArrowRight size={13} weight="bold"/>
              </Link>
            </div>
          )}

          {/* Share bar */}
          <div className="mt-16 pt-8 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Clock size={13}/> Published {new Date(p.published_at).toLocaleDateString()} · {p.view_count} views
            </div>
            <Link to={meta.basePath} className="text-sm text-slate-700 hover:text-slate-900 inline-flex items-center gap-1.5">
              More {meta.title.toLowerCase()} <ArrowRight size={12}/>
            </Link>
          </div>
        </div>
      </section>

      {showGate && <GateModal p={p} accent={meta.accent} onClose={() => setShowGate(false)}/>}
      <MarketingFooter/>
    </div>
  );
}

// ============================================================================
// DOWNLOAD GATE MODAL (captures lead → serves PDF)
// ============================================================================
function GateModal({ p, accent, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", organization: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/publications/${p.slug}/download`, form);
      toast.success("Thank you! Your download is starting…");
      window.open(r.data.pdf_url, "_blank");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong.");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="gate-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded grid place-items-center hover:bg-slate-100">
          <X size={16}/>
        </button>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: accent }}>Instant download</div>
        <h2 className="font-heading text-2xl mt-2 text-slate-900">Get "{p.title}"</h2>
        <p className="text-sm text-slate-600 mt-2">Tell us a little about you and we'll deliver the PDF immediately.</p>
        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="gate-form">
          <GF label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} required testid="gate-name"/>
          <GF label="Work email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} required testid="gate-email"/>
          <GF label="Organization" v={form.organization} on={(v) => setForm({ ...form, organization: v })}/>
          <GF label="Phone (optional)" v={form.phone} on={(v) => setForm({ ...form, phone: v })}/>
          <button type="submit" disabled={busy} data-testid="gate-submit"
                  className="w-full h-12 rounded-full text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ background: accent }}>
            <DownloadSimple size={13}/> {busy ? "Starting download…" : "Get the PDF"}
          </button>
          <div className="text-[11px] text-slate-500 text-center">
            By submitting, you agree to our <Link to="/privacy" className="underline">privacy policy</Link>.
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// Primitives
// ============================================================================
const FilterChip = ({ active, onClick, children }) => (
  <button onClick={onClick} className={`h-7 px-3 rounded-full text-xs font-medium transition ${
    active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
  }`}>
    {children}
  </button>
);

const FeaturedCard = ({ p, basePath, accent }) => (
  <Link to={`${basePath}/${p.slug}`} data-testid={`pub-card-${p.slug}`}
        className="group block bg-white rounded-2xl ring-1 ring-slate-200 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition">
    {p.cover_url ? (
      <div className="aspect-[16/9] overflow-hidden bg-slate-100">
        <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700"/>
      </div>
    ) : (
      <div className="aspect-[16/9]" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}40)` }}/>
    )}
    <div className="p-7">
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">
        {p.category && <span className="inline-flex items-center gap-1"><Tag size={10}/> {p.category}</span>}
        {p.published_at && <span>{new Date(p.published_at).toLocaleDateString()}</span>}
      </div>
      <h3 className="font-heading text-2xl mt-3 text-slate-900 leading-tight">{p.title}</h3>
      {p.summary && <p className="text-sm text-slate-600 mt-3 leading-relaxed line-clamp-3">{p.summary}</p>}
      <div className="mt-5 text-sm font-semibold inline-flex items-center gap-1.5 group-hover:gap-2 transition-all" style={{ color: accent }}>
        Read more <ArrowRight size={12} weight="bold"/>
      </div>
    </div>
  </Link>
);

const PubCard = ({ p, basePath, accent }) => (
  <Link to={`${basePath}/${p.slug}`} data-testid={`pub-card-${p.slug}`}
        className="group block bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition">
    {p.cover_url ? (
      <div className="aspect-[16/9] overflow-hidden bg-slate-100">
        <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700"/>
      </div>
    ) : (
      <div className="aspect-[16/9]" style={{ background: `linear-gradient(135deg, ${accent}20, ${accent}05)` }}/>
    )}
    <div className="p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-500">
        {p.category && <><Tag size={9}/> {p.category}</>}
      </div>
      <h3 className="font-heading text-lg mt-2 text-slate-900 leading-snug line-clamp-2">{p.title}</h3>
      {p.summary && <p className="text-xs text-slate-600 mt-2 leading-relaxed line-clamp-2">{p.summary}</p>}
      <div className="mt-4 flex items-center justify-between text-[10px] text-slate-500">
        <span>{p.published_at && new Date(p.published_at).toLocaleDateString()}</span>
        <span className="font-semibold inline-flex items-center gap-1" style={{ color: accent }}>Read <ArrowRight size={10} weight="bold"/></span>
      </div>
    </div>
  </Link>
);

const GF = ({ label, v, on, type = "text", required, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-slate-500 block mb-1">{label}</label>
    <input type={type} value={v || ""} onChange={(e) => on(e.target.value)}
           required={required} data-testid={testid}
           className="w-full h-11 px-3 border border-slate-300 rounded-md text-sm outline-none focus:border-slate-800"/>
  </div>
);
